import React, { useState, useEffect } from 'react';
import { 
  User, 
  Mail, 
  MapPin, 
  Building2, 
  Calendar, 
  Bell, 
  BellOff,
  AlertTriangle,
  Award,
  TrendingUp,
  Plus,
  ChevronDown,
  CheckCircle,
  Circle,
  X
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import BackButton from './BackButton';
import TierProgressBar from './TierProgressBar';
import CertificationCard from './CertificationCard';
import { LoadingPage, SkeletonCard } from './LoadingStates';
import { NoCertificationsEmpty, ErrorState } from './EmptyStates';
import { 
  getTechnicianProfile, 
  uploadCertification, 
  pauseNotifications, 
  resumeNotifications,
  getAvailableCertifications,
  addCertificationToProfile 
} from '../services/api';
import { TechnicianProfile as TechnicianProfileType, CertificationUpload, Certification } from '../types';

interface TechnicianProfileProps {
  userId?: number;
  isManager?: boolean;
  canEdit?: boolean;
}

const TechnicianProfile: React.FC<TechnicianProfileProps> = ({ 
  userId: propUserId, 
  isManager = false,
  canEdit = true 
}) => {
  const { user } = useAuth();
  const userId = propUserId || user?.id;
  
  const [profile, setProfile] = useState<TechnicianProfileType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [pausingNotifications, setPausingNotifications] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [availableCertifications, setAvailableCertifications] = useState<Certification[]>([]);
  const [selectedCertId, setSelectedCertId] = useState<number | null>(null);
  const [certificationNumber, setCertificationNumber] = useState('');
  const [obtainedDate, setObtainedDate] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [addingCert, setAddingCert] = useState(false);

  useEffect(() => {
    if (userId) {
      fetchProfile();
    }
  }, [userId]);

  const fetchProfile = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await getTechnicianProfile(userId);
      setProfile(response.data);
    } catch (err: any) {
      console.error('Error fetching technician profile:', err);
      setError(err.response?.data?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadCertification = async (data: CertificationUpload) => {
    try {
      setUploading(true);
      await uploadCertification(data);
      await fetchProfile(); // Refresh the profile
    } catch (err: any) {
      console.error('Error uploading certification:', err);
      throw err; // Re-throw to be handled by the CertificationCard
    } finally {
      setUploading(false);
    }
  };

  const handleToggleNotifications = async () => {
    if (!profile?.tier || !userId) return;

    try {
      setPausingNotifications(true);
      
      if (profile.tier.notifications_paused) {
        await resumeNotifications(userId);
      } else {
        // Pause for 30 days by default
        const pauseUntil = new Date();
        pauseUntil.setDate(pauseUntil.getDate() + 30);
        await pauseNotifications(userId, pauseUntil);
      }
      
      await fetchProfile(); // Refresh the profile
    } catch (err: any) {
      console.error('Error toggling notifications:', err);
      alert('Failed to update notification settings');
    } finally {
      setPausingNotifications(false);
    }
  };

  if (loading) {
    return <LoadingPage message="Loading technician profile..." />;
  }

  if (error || !profile) {
    return (
      <div className="container mx-auto px-4 py-8">
        <BackButton className="mb-6" />
        <ErrorState 
          message={error || 'Profile not found'} 
          onRetry={fetchProfile}
        />
      </div>
    );
  }

  const handleAddCertification = async () => {
    if (!selectedCertId || !userId || !certificationNumber.trim()) return;
    
    try {
      setAddingCert(true);
      await addCertificationToProfile({
        certification_id: selectedCertId,
        certification_number: certificationNumber.trim(),
        obtained_date: obtainedDate || undefined,
        expiration_date: expirationDate || undefined
      });
      
      await fetchProfile(); // Refresh profile
      setShowAddModal(false);
      setSelectedCertId(null);
      setCertificationNumber('');
      setObtainedDate('');
      setExpirationDate('');
      alert('Certification added successfully!');
    } catch (err: any) {
      console.error('Error adding certification:', err);
      alert(err.response?.data?.message || 'Failed to add certification');
    } finally {
      setAddingCert(false);
    }
  };

  const loadAvailableCertifications = async () => {
    if (!userId) return;
    
    try {
      const response = await getAvailableCertifications(userId);
      setAvailableCertifications(response.data);
    } catch (err: any) {
      console.error('Error loading available certifications:', err);
    }
  };

  // Calculate tier progress and requirements for NEXT tier
  const currentTier = profile.tier?.current_tier || 0;
  const nextTier = Math.min(currentTier + 1, 3);
  
  // Get requirements for the next tier
  const nextTierRequirements = (profile.tier_requirements || [])
    .filter(req => req.tier_level === nextTier)
    .map(req => ({
      name: req.certification?.code 
        ? `${req.certification.name} ${req.certification.code}`
        : req.certification?.name || 'Unknown Certification',
      certification_id: req.certification_id,
      completed: (profile.certifications || []).some(cert => 
        cert.certification_id === req.certification_id && 
        cert.is_valid &&
        (!cert.expiration_date || new Date(cert.expiration_date) > new Date())
      ),
      required: req.is_required
    }));

  const completedRequired = nextTierRequirements.filter(req => req.required && req.completed).length;
  const totalRequired = nextTierRequirements.filter(req => req.required).length;
  const tierProgress = totalRequired > 0 ? (completedRequired / totalRequired) * 100 : 0;
  
  // Get all tier requirements to show proper tier names
  const allTierRequirements = profile.tier_requirements || [];

  // Group certifications by tier
  const certificationsByTier = (profile.certifications || []).reduce((acc, cert) => {
    const tier = cert.certification?.tier_level || 1;
    if (!acc[tier]) acc[tier] = [];
    acc[tier].push(cert);
    return acc;
  }, {} as Record<number, typeof profile.certifications>);

  const upcomingExpirations = profile.upcoming_expirations || [];

  return (
    <div className="container mx-auto px-4 py-8">
      {isManager && <BackButton className="mb-6" />}
      
      {/* Profile Header */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-gray-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{profile.user.name}</h1>
              <div className="flex items-center space-x-4 mt-2 text-gray-400">
                <div className="flex items-center space-x-1">
                  <Mail className="w-4 h-4" />
                  <span>{profile.user.email}</span>
                </div>
                {profile.user.employee_id && (
                  <div className="flex items-center space-x-1">
                    <span>ID: {profile.user.employee_id}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-4 mt-1 text-gray-400">
                {profile.user.branch && (
                  <div className="flex items-center space-x-1">
                    <MapPin className="w-4 h-4" />
                    <span>{profile.user.branch}</span>
                  </div>
                )}
                {profile.user.department && (
                  <div className="flex items-center space-x-1">
                    <Building2 className="w-4 h-4" />
                    <span>{profile.user.department}</span>
                  </div>
                )}
                {profile.user.hire_date && (
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>Hired: {new Date(profile.user.hire_date).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Notification Toggle */}
          {canEdit && !isManager && (
            <button
              onClick={handleToggleNotifications}
              disabled={pausingNotifications}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                profile.tier?.notifications_paused
                  ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                  : 'bg-gray-600 hover:bg-gray-700 text-gray-300'
              }`}
            >
              {profile.tier?.notifications_paused ? (
                <>
                  <BellOff className="w-4 h-4" />
                  <span>Notifications Paused</span>
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4" />
                  <span>Notifications On</span>
                </>
              )}
            </button>
          )}
        </div>

        {/* Revenue Display */}
        {profile.revenue_data && profile.revenue_data.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {(() => {
                const currentYear = new Date().getFullYear();
                const currentMonth = new Date().getMonth();
                
                // Get most recent month's data
                const mostRecentData = profile.revenue_data.reduce((latest, current) => {
                  const currentDate = new Date(current.month);
                  const latestDate = new Date(latest.month);
                  return currentDate > latestDate ? current : latest;
                });
                
                // Calculate yearly total (from January 1st to current date)
                const yearlyRevenue = profile.revenue_data
                  .filter(data => {
                    const dataDate = new Date(data.month);
                    return dataDate.getFullYear() === currentYear;
                  })
                  .reduce((total, data) => total + (data.gross_revenue || 0), 0);
                
                const yearlyPay = profile.revenue_data
                  .filter(data => {
                    const dataDate = new Date(data.month);
                    return dataDate.getFullYear() === currentYear;
                  })
                  .reduce((total, data) => total + (data.pay_amount || 0), 0);
                
                // Calculate Rev/Pay ratio for most recent month
                const currentRevPayRatio = mostRecentData.pay_amount && mostRecentData.pay_amount > 0
                  ? (mostRecentData.gross_revenue || 0) / mostRecentData.pay_amount
                  : null;
                
                // Calculate yearly Rev/Pay ratio
                const yearlyRevPayRatio = yearlyPay > 0 ? yearlyRevenue / yearlyPay : null;
                
                const formatCurrency = (amount: number) => 
                  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
                
                const formatRatio = (ratio: number | null) => {
                  if (!ratio) return 'N/A';
                  const ratioClass = ratio >= 35 ? 'text-green-400' :
                                   ratio >= 30 ? 'text-yellow-400' :
                                   ratio >= 25 ? 'text-orange-400' :
                                   'text-red-400';
                  return `${ratio.toFixed(2)}:1`;
                };
                
                const getRatioColorClass = (ratio: number | null) => {
                  if (!ratio) return 'text-gray-400';
                  return ratio >= 35 ? 'text-green-400' :
                         ratio >= 30 ? 'text-yellow-400' :
                         ratio >= 25 ? 'text-orange-400' :
                         'text-red-400';
                };
                
                return (
                  <>
                    {/* Monthly Revenue */}
                    <div className="bg-gray-700 rounded-lg p-3">
                      <div className="flex items-center space-x-2 mb-1">
                        <TrendingUp className="w-4 h-4 text-blue-400" />
                        <span className="text-sm font-medium text-gray-300">Monthly Revenue</span>
                      </div>
                      <div className="text-lg font-bold text-white">
                        {formatCurrency(mostRecentData.gross_revenue || 0)}
                      </div>
                      <div className="text-xs text-gray-400">
                        {new Date(mostRecentData.month).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short' 
                        })}
                      </div>
                    </div>
                    
                    {/* Monthly Pay */}
                    <div className="bg-gray-700 rounded-lg p-3">
                      <div className="flex items-center space-x-2 mb-1">
                        <TrendingUp className="w-4 h-4 text-green-400" />
                        <span className="text-sm font-medium text-gray-300">Monthly Pay</span>
                      </div>
                      <div className="text-lg font-bold text-white">
                        {formatCurrency(mostRecentData.pay_amount || 0)}
                      </div>
                      <div className="text-xs text-gray-400">
                        {new Date(mostRecentData.month).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short' 
                        })}
                      </div>
                    </div>
                    
                    {/* Rev/Pay Ratio */}
                    <div className="bg-gray-700 rounded-lg p-3">
                      <div className="flex items-center space-x-2 mb-1">
                        <Award className="w-4 h-4 text-purple-400" />
                        <span className="text-sm font-medium text-gray-300">Rev/Pay Ratio</span>
                      </div>
                      <div className={`text-lg font-bold ${getRatioColorClass(currentRevPayRatio)}`}>
                        {formatRatio(currentRevPayRatio)}
                      </div>
                      <div className="text-xs text-gray-400">Current Month</div>
                    </div>
                    
                    {/* Yearly Revenue */}
                    <div className="bg-gray-700 rounded-lg p-3">
                      <div className="flex items-center space-x-2 mb-1">
                        <Calendar className="w-4 h-4 text-yellow-400" />
                        <span className="text-sm font-medium text-gray-300">Yearly Revenue</span>
                      </div>
                      <div className="text-lg font-bold text-white">
                        {formatCurrency(yearlyRevenue)}
                      </div>
                      <div className="text-xs text-gray-400">{currentYear} YTD</div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* Upcoming Expirations Alert */}
      {upcomingExpirations.length > 0 && (
        <div className="bg-red-900/30 border border-red-500/30 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <h3 className="text-lg font-semibold text-red-400">
              Upcoming Expirations
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {upcomingExpirations.map((cert) => (
              <div key={cert.id} className="bg-red-900/20 rounded p-3">
                <div className="font-medium text-white">
                  {cert.certification?.code 
                    ? `${cert.certification.name} ${cert.certification.code}`
                    : cert.certification?.name}
                </div>
                <div className="text-sm text-red-300">
                  Expires: {cert.expiration_date ? new Date(cert.expiration_date).toLocaleDateString() : 'Unknown'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Current Tier and Progress */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center">
              <Award className={`w-6 h-6 mr-2 ${
                currentTier === 0 ? 'text-gray-400' :
                currentTier === 1 ? 'text-blue-400' :
                currentTier === 2 ? 'text-purple-400' :
                'text-yellow-400'
              }`} />
              Current Tier: {currentTier === 0 ? 'New Hire' : `Tier ${currentTier}`}
            </h2>
            {currentTier < 3 && (
              <p className="text-gray-400 mt-1">
                Complete {totalRequired - completedRequired} more certification{totalRequired - completedRequired !== 1 ? 's' : ''} to advance to {nextTier === 0 ? 'New Hire' : `Tier ${nextTier}`}
              </p>
            )}
          </div>
          {canEdit && !isManager && (
            <button
              onClick={() => {
                setShowAddModal(true);
                loadAvailableCertifications();
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Certification</span>
            </button>
          )}
        </div>
        
        {/* Progress to Next Tier */}
        {currentTier < 3 && (
          <>
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-400 mb-1">
                <span>Progress to {nextTier === 0 ? 'New Hire' : `Tier ${nextTier}`}</span>
                <span>{completedRequired}/{totalRequired} Required</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-3">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300" 
                  style={{ width: `${tierProgress}%` }}
                />
              </div>
            </div>
            
            {/* Requirements for Next Tier */}
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-2">Requirements for {nextTier === 0 ? 'New Hire' : `Tier ${nextTier}`}:</h3>
              <div className="space-y-1">
                {nextTierRequirements.length === 0 ? (
                  <p className="text-sm text-gray-400">No specific requirements for this tier. Continue earning certifications to progress.</p>
                ) : (
                  <>
                    {/* Required Certifications */}
                    {nextTierRequirements.filter(req => req.required).length > 0 && (
                      <>
                        <p className="text-xs text-gray-400 font-semibold mb-1">Required:</p>
                        {nextTierRequirements.filter(req => req.required).map((req, idx) => (
                          <div key={`req-${idx}`} className="flex items-center space-x-2 text-sm ml-2">
                            {req.completed ? (
                              <CheckCircle className="w-4 h-4 text-green-400" />
                            ) : (
                              <Circle className="w-4 h-4 text-gray-500" />
                            )}
                            <span className={req.completed ? 'text-green-400' : 'text-gray-400'}>
                              {req.name}
                            </span>
                          </div>
                        ))}
                      </>
                    )}
                    
                    {/* Optional Certifications */}
                    {nextTierRequirements.filter(req => !req.required).length > 0 && (
                      <>
                        <p className="text-xs text-gray-400 font-semibold mb-1 mt-2">Optional:</p>
                        {nextTierRequirements.filter(req => !req.required).map((req, idx) => (
                          <div key={`opt-${idx}`} className="flex items-center space-x-2 text-sm ml-2">
                            {req.completed ? (
                              <CheckCircle className="w-4 h-4 text-green-400" />
                            ) : (
                              <Circle className="w-4 h-4 text-gray-500" />
                            )}
                            <span className={req.completed ? 'text-green-400' : 'text-gray-400'}>
                              {req.name}
                            </span>
                          </div>
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
        
        {currentTier === 3 && (
          <p className="text-green-400 font-medium">
            🎉 Congratulations! You have achieved the highest tier level.
          </p>
        )}
      </div>

      {/* Certifications by Tier */}
      <div className="space-y-6">
        {[0, 1, 2, 3].map((tier) => {
          const tierCerts = certificationsByTier[tier] || [];
          if (tierCerts.length === 0) return null;

          return (
            <div key={tier}>
              <div className="flex items-center space-x-2 mb-4">
                <Award className={`w-5 h-5 ${
                  tier === 0 ? 'text-gray-400' :
                  tier === 1 ? 'text-blue-400' :
                  tier === 2 ? 'text-purple-400' :
                  'text-yellow-400'
                }`} />
                <h2 className="text-xl font-semibold text-white">
                  {tier === 0 ? 'New Hire' : `Tier ${tier}`} Certifications
                </h2>
                <span className="text-gray-400">
                  ({tierCerts.length})
                </span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {tierCerts.map((certification) => (
                  <CertificationCard
                    key={certification.id}
                    certification={certification}
                    isManager={isManager}
                    canEdit={canEdit}
                    onUpload={handleUploadCertification}
                    className="h-full"
                  />
                ))}
              </div>
            </div>
          );
        })}

        {/* Empty State */}
        {Object.keys(certificationsByTier).length === 0 && (
          <NoCertificationsEmpty />
        )}
      </div>

      {/* Loading overlay for uploads */}
      {uploading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="text-white">Uploading certification...</span>
          </div>
        </div>
      )}

      {/* Add Certification Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Add Certification</h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedCertId(null);
                  setCertificationNumber('');
                  setObtainedDate('');
                  setExpirationDate('');
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Certification Selection */}
              <div>
                <label className="block text-gray-300 mb-2">Select Certification</label>
                <select
                  value={selectedCertId || ''}
                  onChange={(e) => setSelectedCertId(Number(e.target.value))}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Choose a certification...</option>
                  {[0, 1, 2, 3].map(tier => {
                    const tierCerts = availableCertifications.filter(cert => cert.tier_level === tier);
                    if (tierCerts.length === 0) return null;
                    
                    return (
                      <optgroup key={tier} label={tier === 0 ? 'New Hire' : `Tier ${tier}`}>
                        {tierCerts.map(cert => (
                          <option key={cert.id} value={cert.id}>
                            {cert.name} {cert.code ? `(${cert.code})` : ''}
                          </option>
                        ))}
                      </optgroup>
                    );
                  })}
                </select>
              </div>

              {/* Certification Number */}
              <div>
                <label className="block text-gray-300 mb-2">Certification Number <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  value={certificationNumber}
                  onChange={(e) => setCertificationNumber(e.target.value)}
                  placeholder="Enter certification number (e.g., ABC123, 12345)"
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-400 mt-1">This number appears on your certification document</p>
              </div>

              {/* Obtained Date */}
              <div>
                <label className="block text-gray-300 mb-2">Date Obtained</label>
                <input
                  type="date"
                  value={obtainedDate}
                  onChange={(e) => setObtainedDate(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Expiration Date */}
              <div>
                <label className="block text-gray-300 mb-2">Expiration Date (Optional)</label>
                <input
                  type="date"
                  value={expirationDate}
                  onChange={(e) => setExpirationDate(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {selectedCertId && (() => {
                  const cert = availableCertifications.find(c => c.id === selectedCertId);
                  return cert?.validity_period ? (
                    <p className="text-sm text-gray-400 mt-1">
                      This certification typically expires after {cert.validity_period} months
                    </p>
                  ) : null;
                })()}
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setSelectedCertId(null);
                    setCertificationNumber('');
                    setObtainedDate('');
                    setExpirationDate('');
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddCertification}
                  disabled={!selectedCertId || !certificationNumber.trim() || addingCert}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center space-x-2"
                >
                  {addingCert ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Adding...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      <span>Add Certification</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechnicianProfile;
